/**
 * ロジック図に出す文言を訳から組むフック。種類のタグと、実データの図の文言（終了 ・ 遷移先 ・
 * 返り値 ・ 表の見出し ・ 並べ替えの向き）を 1 か所で引く。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ロジック図の種類のタグと、実データの図に出す文言を、今の言語の訳から組んで画面へ渡す。
 * @graph-connects none
 */
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { displayLabel } from '../../components/spec/UntranslatedBadge.js';
import type { LogicKindLabels } from './logic-build.js';
import type { LogicFromRulesTexts } from './logic-from-rules.js';

/**
 * 種類のタグの文言。
 *
 * @returns 種類ごとのタグの文言
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ロジック図の種類のタグ（入力・業務ルール・業務ルールの出力・分岐・作用・終了）の文言を今の言語で返す。
 * @graph-connects none
 */
export function useLogicKindLabels(): LogicKindLabels {
  const { t } = useTranslation();
  return useMemo(
    () => ({
      input: t('logicDiagram.kind.input'),
      rule: t('logicDiagram.kind.rule'),
      ruleOutput: t('logicDiagram.kind.ruleOutput'),
      branch: t('logicDiagram.kind.branch'),
      effect: t('logicDiagram.kind.effect'),
      end: t('logicDiagram.kind.end'),
    }),
    [t],
  );
}

/**
 * 実データのロジック図に出す文言。
 *
 * @returns 実データのロジック図の文言
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 実データのロジック図に出す文言（それ以外・終了・遷移先・返り値・分岐の条件のセル）を今の言語で返す。
 * @graph-connects displayLabel [calls] 表示名として実際に描く文字列を決める
 */
export function useLogicFromRulesTexts(): LogicFromRulesTexts {
  const { t } = useTranslation();
  return useMemo(
    () => ({
      label: (value, untranslated) => displayLabel(value, t, untranslated),
      otherwise: t('logicDiagram.real.otherwise'),
      take: (count) => t('logicDiagram.real.take', { count }),
      endTitle: (result) => t('logicDiagram.real.endTitle', { result }),
      nextBusiness: (business) => t('logicDiagram.real.nextBusiness', { business }),
      stop: t('logicDiagram.real.stop'),
      exit: t('logicDiagram.real.exit'),
      back: (business) => t('logicDiagram.real.back', { business }),
      returns: (value) => t('logicDiagram.real.returns', { value }),
      errorReturns: (result) => t('logicDiagram.real.errorReturns', { result }),
      markEffect: (property, value) => t('logicDiagram.real.markEffect', { property, value }),
      any: t('logicDiagram.real.any'),
      range: (comparator, operand) => t(`logicDiagram.real.range.${comparator}`, { operand }),
    }),
    [t],
  );
}
